import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { nwFormSubmissionSchema, NW_OPERATING_MARKETS } from '@qdf/shared';
import type { NwFormSubmissionInput, FormSubmissionInput, FormSubmissionResponse } from '@qdf/shared';
import { useNwFormSubmission } from '../../hooks/useNwFormSubmission.js';
import { fetchNwFormConfig } from '../../api/nw-submissions.js';
import { CompanyInfoSection } from '../form/CompanyInfoSection.js';
import { CreditRequestSection } from '../form/CreditRequestSection.js';
import { ConsentSection } from '../form/ConsentSection.js';

// Cloudflare Turnstile explicit-render API (script injected on demand).
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
    };
    __qdfTurnstileOnload?: () => void;
  }
}

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__qdfTurnstileOnload&render=explicit';

interface NwIntakeFormProps {
  onSuccess: (response: FormSubmissionResponse) => void;
}

export function NwIntakeForm({ onSuccess }: NwIntakeFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NwFormSubmissionInput>({
    resolver: zodResolver(nwFormSubmissionSchema),
    defaultValues: {
      consentGiven: false as unknown as true,
      fullName: '',
      entityName: '',
      dba: '',
      email: '',
      phone: '',
      mailingAddress1: '',
      mailingAddress2: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: '',
      locAmountRequested: undefined as unknown as number,
      marketsOperating: '',
      avgAnnualTransactions: undefined as unknown as number,
      avgPurchasePrice: undefined as unknown as number,
      preferredTitleCompany: '',
      website: '',
    },
  });

  // The shared form sections are typed for the Left Main input shape.
  // NwFormSubmissionInput differs only by the fields the sections never
  // touch (no partnerSubmissionId, plus the honeypot), so the cast is safe.
  const sectionRegister = register as unknown as UseFormRegister<FormSubmissionInput>;
  const sectionErrors = errors as FieldErrors<FormSubmissionInput>;

  const { submit, isSubmitting, error: submitError, fieldErrors } = useNwFormSubmission();

  // ── Cloudflare Turnstile (rendered only when this env has a site key) ──
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    fetchNwFormConfig()
      .then((cfg) => setTurnstileSiteKey(cfg.turnstileSiteKey))
      .catch(() => setTurnstileSiteKey(null)); // config unreachable → no widget
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey) return;
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      window.__qdfTurnstileOnload = renderWidget;
      if (!document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`)) {
        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
    }
    return () => { cancelled = true; };
  }, [turnstileSiteKey]);

  const turnstileRequired = !!turnstileSiteKey;
  const turnstileReady = !turnstileRequired || !!turnstileToken;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const response = await submit({ ...data, turnstileToken: turnstileToken || undefined });
      onSuccess(response);
    } catch {
      // Error state is handled by useNwFormSubmission hook. Turnstile tokens
      // are single-use — reset the widget so a retry gets a fresh token.
      if (widgetIdRef.current !== null) {
        setTurnstileToken('');
        window.turnstile?.reset(widgetIdRef.current);
      }
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-8" noValidate>
      {/* Honeypot — humans never see or fill this; bots that do are dropped
          server-side. Off-screen (not display:none) so naive bots still fill it. */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 overflow-hidden">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('website')}
        />
      </div>

      <CompanyInfoSection register={sectionRegister} errors={sectionErrors} />
      <CreditRequestSection
        register={sectionRegister}
        errors={sectionErrors}
        pilotNote=""
        markets={NW_OPERATING_MARKETS}
      />
      <ConsentSection register={sectionRegister} errors={sectionErrors} />

      {submitError && (
        <div className="bg-error-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-error-500">{submitError}</p>
          {fieldErrors && (
            <ul className="mt-2 text-xs text-error-500 list-disc list-inside">
              {Object.entries(fieldErrors).map(([field, msgs]) =>
                msgs.map((msg) => (
                  <li key={`${field}-${msg}`}>
                    {field}: {msg}
                  </li>
                )),
              )}
            </ul>
          )}
        </div>
      )}

      {/* Cloudflare Turnstile — human verification (only when configured) */}
      {turnstileRequired && (
        <div className="flex justify-end pt-2">
          <div ref={turnstileRef} />
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isSubmitting || !turnstileReady}
          className={`
            px-8 py-3 rounded-lg font-semibold text-white text-sm transition-all uppercase tracking-wider
            ${isSubmitting || !turnstileReady ? 'bg-gray-400 cursor-not-allowed' : 'shadow-sm hover:shadow'}
          `}
          style={isSubmitting || !turnstileReady ? undefined : { background: 'var(--color-nw-gold)' }}
        >
          {isSubmitting ? 'Submitting...' : !turnstileReady ? 'Complete verification above' : 'Submit Application'}
        </button>
      </div>
    </form>
  );
}
